import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaBriefcase,
    FaTasks,
    FaChartLine,
    FaCalendarCheck,
    FaCalendarAlt,
    FaBell,
    FaArrowRight,
    FaMapMarkerAlt,
    FaHourglassHalf,
    FaCheckCircle,
    FaTimesCircle,
    FaBan
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import { showError } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import { API_URL } from '../../../config';
import './JobSeekerOverview.css';

const STATUS_CONFIG = {
    Pending: { label: 'Under Review', color: '#92400E', bg: '#FEF3C7' },
    Shortlisted: { label: 'Shortlisted', color: '#065F46', bg: '#D1FAE5' },
    Rejected: { label: 'Not Selected', color: '#B91C1C', bg: '#FEE2E2' },
    'Interview Scheduled': { label: 'Interview Scheduled', color: '#1D4ED8', bg: '#DBEAFE' },
    'Interview Completed': { label: 'Interview Done', color: '#065F46', bg: '#D1FAE5' },
    Withdrawn: { label: 'Withdrawn', color: '#475569', bg: '#F1F5F9' }
};

const LOGO_COLORS = [
    ['#1E3A8A', '#3B82F6'], ['#065F46', '#10B981'], ['#7C2D12', '#F97316'],
    ['#4C1D95', '#8B5CF6'], ['#831843', '#EC4899'], ['#1E3A8A', '#6366F1'],
    ['#134E4A', '#14B8A6'], ['#713F12', '#EAB308'], ['#0C4A6E', '#0EA5E9'], ['#3F1F1F', '#EF4444']
];
const getLogoColor = (name = '') => LOGO_COLORS[(name.charCodeAt(0) || 65) % LOGO_COLORS.length];

/* Column definitions — exactly mirrors Recruiter's jobColumns pattern */
const appColumns = [
    {
        header: 'Company',
        key: 'company',
        render: (row) => {
            const companyName = row.jobId?.company || '—';
            const [from, to] = getLogoColor(companyName);
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div
                        style={{
                            width: '32px', height: '32px', borderRadius: '6px', overflow: 'hidden', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: row.jobId?.companyId?.logoUrl ? '#fff' : `linear-gradient(135deg, ${from}, ${to})`,
                            color: '#fff', fontSize: '14px', fontWeight: 'bold'
                        }}
                    >
                        {row.jobId?.companyId?.logoUrl ? (
                            <img
                                src={`${API_URL}${row.jobId.companyId.logoUrl}`}
                                alt={companyName}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        ) : (
                            <span>{companyName.charAt(0)}</span>
                        )}
                    </div>
                    <span className="job-title-text" style={{ fontWeight: 700, color: '#1e3a8a' }}>
                        {companyName}
                    </span>
                </div>
            );
        }
    },
    {
        header: 'Job Title',
        key: 'title',
        render: (row) => (
            <span style={{ fontWeight: 600, color: '#334155' }}>
                {row.jobId?.title || '—'}
            </span>
        )
    },
    {
        header: 'Location',
        key: 'location',
        render: (row) => (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '13px' }}>
                <FaMapMarkerAlt style={{ color: '#1D4ED8', flexShrink: 0 }} />
                {row.jobId?.location || '—'}
            </span>
        )
    },
    {
        header: 'Applied On',
        key: 'appliedAt',
        render: (row) => (
            <span style={{ color: '#64748b', fontSize: '13px' }}>
                {row.appliedAt
                    ? new Date(row.appliedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                    : '—'}
            </span>
        )
    },
    {
        header: 'Status',
        key: 'status',
        render: (row) => {
            const cfg = STATUS_CONFIG[row.status] || STATUS_CONFIG.Pending;
            return (
                <span
                    className={`status-badge`}
                    style={{
                        background: cfg.bg,
                        color: cfg.color,
                        padding: '5px 13px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        whiteSpace: 'nowrap',
                        display: 'inline-block'
                    }}
                >
                    {cfg.label}
                </span>
            );
        }
    }
];

const JobSeekerOverview = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ appliedJobs: 0, pendingApplications: 0, shortlisted: 0, interviews: 0 });
    const [upcomingInterviews, setUpcomingInterviews] = useState([]);
    const [recentNotifications, setRecentNotifications] = useState([]);
    const [recentApplications, setRecentApplications] = useState([]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const token = localStorage.getItem('token');
                const config = { headers: { Authorization: `Bearer ${token}` } };

                const [appRes, interviewRes, notifRes] = await Promise.all([
                    axios.get('/api/student/applications', config),
                    axios.get('/api/student/interviews', config).catch(() => ({ data: [] })),
                    axios.get('/api/notifications', config).catch(() => ({ data: [] }))
                ]);

                const apps = appRes.data;
                const interviews = interviewRes.data;

                setStats({
                    appliedJobs: apps.length,
                    pendingApplications: apps.filter(a => a.status === 'Pending').length,
                    shortlisted: apps.filter(a => a.status === 'Shortlisted').length,
                    interviews: interviews.filter(i => i.status !== 'Completed').length
                });

                const upcoming = interviews
                    .filter(i => new Date(i.scheduledDate) >= new Date())
                    .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate))
                    .slice(0, 4);
                setUpcomingInterviews(upcoming);

                setRecentNotifications(notifRes.data.slice(0, 5));

                const sorted = [...apps].sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt));
                setRecentApplications(sorted.slice(0, 5));

                setLoading(false);
            } catch (err) {
                showError('Dashboard Error', 'Failed to synchronize live data feed');
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    if (loading) return <Loader />;

    return (
        <div className="js-jobseeker-overview js-fade-in">
            <div className="js-dashboard-header">
                <h1 className="js-page-title">The Hiring Hub</h1>
                <p className="js-page-subtitle">Track your career progression and hiring pipeline metrics.</p>
            </div>

            {/* KPI Grid */}
            <div className="js-kpi-grid">
                <div className="js-kpi-card" onClick={() => navigate('/jobseeker-dashboard/applications')}>
                    <div className="js-kpi-header">
                        <span className="js-kpi-title">Jobs Applied</span>
                        <div className="js-kpi-icon"><FaBriefcase /></div>
                    </div>
                    <h2 className="js-kpi-value">{stats.appliedJobs}</h2>
                    <div className="js-kpi-footer">
                        <span className="js-highlight">All time</span> applications
                    </div>
                </div>

                <div className="js-kpi-card" onClick={() => navigate('/jobseeker-dashboard/applications')}>
                    <div className="js-kpi-header">
                        <span className="js-kpi-title">Under Review</span>
                        <div className="js-kpi-icon"><FaTasks /></div>
                    </div>
                    <h2 className="js-kpi-value">{stats.pendingApplications}</h2>
                    <div className="js-kpi-footer">
                        Awaiting <span className="js-highlight">recruiter</span> response
                    </div>
                </div>

                <div className="js-kpi-card" onClick={() => navigate('/jobseeker-dashboard/applications')}>
                    <div className="js-kpi-header">
                        <span className="js-kpi-title">Shortlisted</span>
                        <div className="js-kpi-icon"><FaChartLine /></div>
                    </div>
                    <h2 className="js-kpi-value">{stats.shortlisted}</h2>
                    <div className="js-kpi-footer">
                        Moving to <span className="js-highlight">next stage</span>
                    </div>
                </div>

                <div className="js-kpi-card" onClick={() => navigate('/jobseeker-dashboard/applications')}>
                    <div className="js-kpi-header">
                        <span className="js-kpi-title">Interviews</span>
                        <div className="js-kpi-icon"><FaCalendarCheck /></div>
                    </div>
                    <h2 className="js-kpi-value">{stats.interviews}</h2>
                    <div className="js-kpi-footer">
                        Scheduled <span className="js-highlight">upcoming</span>
                    </div>
                </div>
            </div>

            {/* Upcoming Interviews + Latest Alerts */}
            <div className="js-overview-grid">
                <div className="js-content-card">
                    <div className="js-card-header">
                        <h3 className="js-card-title">Upcoming Interviews</h3>
                        <span className="js-view-all" onClick={() => navigate('/jobseeker-dashboard/applications')}>
                            View All <FaArrowRight />
                        </span>
                    </div>
                    <div className="js-data-list-container">
                        {upcomingInterviews.length > 0 ? (
                            upcomingInterviews.map((interview) => (
                                <div key={interview._id} className="js-data-item">
                                    <div className="js-item-date-box">
                                        <span className="js-day">{new Date(interview.scheduledDate).getDate()}</span>
                                        <span className="js-month">{new Date(interview.scheduledDate).toLocaleString('default', { month: 'short' })}</span>
                                    </div>
                                    <div className="js-item-info" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div
                                            style={{
                                                width: '36px', height: '36px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                background: interview.jobId?.companyId?.logoUrl ? '#fff' : `linear-gradient(135deg, ${getLogoColor(interview.jobId?.company || '')[0]}, ${getLogoColor(interview.jobId?.company || '')[1]})`,
                                                color: '#fff', fontSize: '15px', fontWeight: 'bold'
                                            }}
                                        >
                                            {interview.jobId?.companyId?.logoUrl ? (
                                                <img
                                                    src={`${API_URL}${interview.jobId.companyId.logoUrl}`}
                                                    alt={interview.jobId?.company}
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <span>{(interview.jobId?.company || 'C').charAt(0)}</span>
                                            )}
                                        </div>
                                        <div>
                                            <h4 style={{ margin: 0 }}>{interview.jobId?.title || 'Job Interview'}</h4>
                                            <p style={{ margin: 0, fontSize: '12px' }}>{interview.jobId?.company || 'Company'} • {interview.timeSlot}</p>
                                        </div>
                                    </div>
                                    <button className="js-primary-btn-sm" onClick={() => navigate('/jobseeker-dashboard/applications')}>Details</button>
                                </div>
                            ))
                        ) : (
                            <div className="js-empty-state-mini">
                                <FaCalendarAlt size={32} color="#E2E8F0" />
                                <p>No upcoming interviews scheduled.</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="js-content-card">
                    <div className="js-card-header">
                        <h3 className="js-card-title">Latest Alerts</h3>
                        <span className="js-view-all" onClick={() => navigate('/jobseeker-dashboard/notifications')}>
                            Inbox <FaArrowRight />
                        </span>
                    </div>
                    <div className="js-data-list-container">
                        {recentNotifications.length > 0 ? (
                            recentNotifications.map((n) => (
                                <div key={n._id} className={`js-notif-item ${!n.isRead ? 'js-unread' : ''}`}>
                                    <div className="js-notif-icon"><FaBell /></div>
                                    <div className="js-notif-content">
                                        <p className="js-notif-msg">{n.message}</p>
                                        <span className="js-notif-time">
                                            {new Date(n.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="js-empty-state-mini">
                                <FaBell size={32} color="#E2E8F0" />
                                <p>No new notifications yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ─── Recent Applications (exact same pattern as Recruiter Active Listings) ─── */}
            <div className="premium-card overview-jobs-card">
                <div className="card-header-with-action">
                    <h3 className="section-title" onClick={() => navigate('/jobseeker-dashboard/applications')} style={{ cursor: 'pointer' }}>Recent Applications</h3>
                    <button
                        className="secondary-btn flex-center"
                        onClick={() => navigate('/jobseeker-dashboard/applications')}
                    >
                        View All <FaArrowRight style={{ marginLeft: '8px' }} />
                    </button>
                </div>
                <DataTable
                    columns={appColumns}
                    data={recentApplications}
                    hidePagination={true}
                    onRowClick={() => navigate('/jobseeker-dashboard/applications')}
                />
            </div>
        </div>
    );
};

export default JobSeekerOverview;
